import { useState, useEffect, useCallback, useContext, createContext, createElement, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN ?? '';
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI ?? window.location.origin;
const TOKEN_URL = COGNITO_DOMAIN ? `https://${COGNITO_DOMAIN}/oauth2/token` : '';

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { email: string; groups?: string[] } | null;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    return JSON.parse(atob(base64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Lift the `groups` claim (Cognito/Entra group membership) off a parsed JWT payload. */
function extractGroups(payload: Record<string, unknown> | null): string[] | undefined {
  const groups = payload?.['groups'];
  return Array.isArray(groups) ? (groups as string[]) : undefined;
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload?.['exp']) return true;
  return (payload['exp'] as number) * 1000 < Date.now();
}

// PKCE helpers
async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface AuthorizeQueryParams {
  clientId: string;
  redirectUri: string;
  codeChallenge: string;
  /** Federated IdP name (e.g. `Entra`). When non-empty, Cognito 302s straight to the IdP. */
  identityProvider?: string;
}

/**
 * Build the Cognito `/oauth2/authorize` query for a PKCE public-client login.
 *
 * When `identityProvider` is a non-empty string, an `identity_provider` param is
 * appended so Cognito redirects straight to the federated IdP, skipping the hosted-UI
 * chooser. When it is empty or undefined the param is omitted and native Cognito login
 * is preserved. Every emitted value is a public PKCE parameter (client_id, redirect_uri,
 * code_challenge/S256, identity_provider) — no secrets.
 */
export function buildAuthorizeQuery({
  clientId,
  redirectUri,
  codeChallenge,
  identityProvider,
}: AuthorizeQueryParams): URLSearchParams {
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  if (identityProvider) {
    params.append('identity_provider', identityProvider);
  }
  return params;
}

/**
 * True only while an OAuth code→token exchange is actually in flight — i.e. a `code`
 * param is present on the URL (the redirect back from Cognito/Entra). A plain signed-out
 * or first visit has no `code`, so it is NOT "loading": we must show the Splash directly
 * rather than a "Signing you in…" indicator that would flash before it.
 */
export function isAuthExchangeInFlight(search: string): boolean {
  return new URLSearchParams(search).has('code');
}

function getStoredAuth(): AuthState {
  const stored = sessionStorage.getItem('auth_id_token');
  if (stored && !isTokenExpired(stored)) {
    // Re-parse the stored id token so a page reload keeps both email and the
    // groups claim (admin state) rather than adding a second stored key.
    const payload = parseJwt(stored);
    const email = (payload?.['email'] as string) ?? sessionStorage.getItem('auth_email') ?? 'authenticated';
    const groups = extractGroups(payload);
    return { token: stored, isAuthenticated: true, isLoading: false, user: { email, groups } };
  }
  // Only "loading" when a code→token exchange is genuinely in flight. Initializing
  // isLoading:true unconditionally made every signed-out/first visit flash the loading
  // indicator for one frame before the Splash — the mirror of the flash this slice removes.
  return { token: null, isAuthenticated: false, isLoading: isAuthExchangeInFlight(window.location.search), user: null };
}

/**
 * True only for a same-origin internal path: a value with exactly one leading
 * `/` that is neither protocol-relative (`//host`) nor backslash-obfuscated
 * (`/\host`). Any absolute URL or scheme-bearing value (e.g. `https://…`,
 * `javascript:…`) lacks that single leading slash and is rejected. Guards the
 * post-login restore against an open redirect driven by a poisoned
 * `auth_return_path` — same-origin navigation only.
 */
function isSafeInternalPath(p: string): boolean {
  return p.length > 1 && p.startsWith('/') && p[1] !== '/' && p[1] !== '\\';
}

function useAuthInternal() {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>(getStoredAuth);

  // Exchange authorization code for tokens on redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code || !TOKEN_URL) {
      // No code in URL — just mark loading as done
      setAuthState((prev) => prev.isLoading ? { ...prev, isLoading: false } : prev);
      return;
    }

    const verifier = sessionStorage.getItem('pkce_verifier');
    if (!verifier) {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    // Immediately clear code and verifier to prevent double-exchange
    // (React StrictMode runs useEffect twice)
    sessionStorage.removeItem('pkce_verifier');
    window.history.replaceState(null, '', window.location.pathname);

    // Exchange code for tokens
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: COGNITO_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: verifier,
    });

    fetch(TOKEN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then((data) => {
        const idToken = data.id_token as string | undefined;
        if (!idToken) {
          setAuthState({ token: null, isAuthenticated: false, isLoading: false, user: null });
          return;
        }

        const payload = parseJwt(idToken);
        const email = (payload?.['email'] as string) ?? 'authenticated';
        const groups = extractGroups(payload);

        sessionStorage.setItem('auth_id_token', idToken);
        sessionStorage.setItem('auth_email', email);

        setAuthState({
          token: idToken,
          isAuthenticated: true,
          isLoading: false,
          user: { email, groups },
        });

        // Restore, via React Router, the full path (+ query) the user was on
        // before login so the deep-linked route — its scope AND its Delivery/
        // Discovery lens — actually renders instead of the '/' overview. Compare
        // against pathname + search so a stored '/?mode=discovery' is recognized
        // as a real return target even when the post-redirect pathname is '/'.
        // navigate(..., { replace: true }) (not push) so Back does not return to
        // the ?code= callback URL. A present-but-unsafe stored path (absolute,
        // protocol-relative, or scheme-bearing — a poisoned value) falls back to
        // '/', so it can never drive an open redirect off-origin.
        const returnPath = sessionStorage.getItem('auth_return_path');
        sessionStorage.removeItem('auth_return_path');
        const currentFullPath = window.location.pathname + window.location.search;
        if (returnPath && returnPath !== '/' && returnPath !== currentFullPath) {
          navigate(isSafeInternalPath(returnPath) ? returnPath : '/', { replace: true });
        }

      })
      .catch(() => {
        setAuthState({ token: null, isAuthenticated: false, isLoading: false, user: null });
      });
  }, []);

  const login = useCallback(async () => {
    if (!COGNITO_DOMAIN || !COGNITO_CLIENT_ID) {
      setAuthState({
        token: 'dev-token',
        isAuthenticated: true,
        isLoading: false,
        user: { email: 'dev@local' },
      });
      return;
    }

    const verifier = await generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem('pkce_verifier', verifier);
    // Save full path + query so the user lands back on the same view
    // (including ?mode=discovery, ?team=, etc.) after the Cognito redirect.
    sessionStorage.setItem('auth_return_path', window.location.pathname + window.location.search);

    // Read the federated IdP from the declared build var so the redirect skips the
    // Cognito hosted-UI chooser and 302s straight to the IdP. Empty/unset -> native login.
    const identityProvider: string = import.meta.env.VITE_IDENTITY_PROVIDER ?? '';
    const params = buildAuthorizeQuery({
      clientId: COGNITO_CLIENT_ID,
      redirectUri: REDIRECT_URI,
      codeChallenge: challenge,
      identityProvider,
    });

    window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('auth_id_token');
    sessionStorage.removeItem('auth_email');
    sessionStorage.removeItem('pkce_verifier');
    setAuthState({
      token: null,
      isAuthenticated: false,
      isLoading: false,
      user: null,
    });

    if (COGNITO_DOMAIN && COGNITO_CLIENT_ID) {
      window.location.href =
        `https://${COGNITO_DOMAIN}/logout?` +
        `client_id=${COGNITO_CLIENT_ID}&` +
        `logout_uri=${encodeURIComponent(REDIRECT_URI)}`;
    }
  }, []);

  return { ...authState, login, logout };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthInternal();
  return createElement(AuthContext.Provider, { value: auth }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
